import type { ApiClient } from '@carbonwebmail/api-client'
import type { ContactFieldDto, SegmentDefinitionDto } from '@carbonwebmail/contracts'
import { useCallback } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { Async } from '../../ui/Async.js'
import { PillButton } from '../../ui/PillButton.js'
import { Problems } from '../../ui/Problems.js'
import { ScreenNote } from '../../ui/ScreenNote.js'
import { useLoaded } from '../../ui/useLoaded.js'
import { reachLabel } from './describeSegment.js'
import { RuleRow } from './RuleRow.js'
import type { Keyed } from './useSegmentBuilder.js'
import { isKeyedGroup, useSegmentBuilder } from './useSegmentBuilder.js'

/**
 * Building a segment.
 *
 * The number in the corner is the point of the screen. A rule list is abstract
 * until something counts it — "not sent for 90 days" meaning twelve people
 * rather than twelve thousand is the difference between sending and not, and
 * after the send is the expensive time to learn it.
 */

/**
 * Loads the segment first, then opens the builder on it.
 *
 * **Two components, and that is the fix.** The builder takes what it opens on
 * once, at its first render — so a page that rendered it while the segment was
 * still arriving would open blank and stay blank. This one renders nothing
 * until it has the answer, which is why the builder below can keep taking an
 * `initial` rather than learning to reset itself.
 *
 * This screen read "Edit segment" and loaded nothing at all until 2026-09-18:
 * opening a segment showed an empty builder, and saving replaced its rules with
 * a default.
 *
 * **It only ever edits.** Making a segment is `NewSegmentDialog` over the
 * board, so there is no `/segments/new` and no blank state to fall back to.
 */
export function SegmentBuilderPage({ client }: { readonly client: ApiClient }) {
  const { segmentId } = useParams()

  const load = useCallback(
    async () => (segmentId === undefined ? undefined : await client.segment(segmentId)),
    [client, segmentId],
  )
  const { state, reload } = useLoaded(load, 'We could not load that segment. Please try again.')

  // The route always carries one; an address without it names no segment.
  if (segmentId === undefined) return <ScreenNote>That segment no longer exists.</ScreenNote>

  return (
    <Async onRetry={() => void reload()} skeleton={<ScreenNote>Loading…</ScreenNote>} state={state}>
      {(found) =>
        found === undefined ? (
          <ScreenNote>That segment no longer exists.</ScreenNote>
        ) : (
          <Builder
            client={client}
            initial={{ name: found.name, definition: found.definition }}
            segmentId={segmentId}
          />
        )
      }
    </Async>
  )
}

function Builder({
  client,
  initial,
  segmentId,
}: {
  readonly client: ApiClient
  readonly initial: { readonly name: string; readonly definition: SegmentDefinitionDto }
  readonly segmentId: string
}) {
  const navigate = useNavigate()

  const builder = useSegmentBuilder({
    client,
    initial,
    segmentId,
    onSaved: () => navigate('/segments'),
  })

  const { state } = builder

  return (
    <>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-bold text-[22px] text-t1 -tracking-[0.01em]">Edit segment</h1>
          <p className="mt-1 text-[13px] text-t2">
            Answered when a campaign is sent, not now — so this stays true as your list changes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            className="rounded-pill border border-line px-5 py-2.5 font-bold text-[14px] text-t2"
            to="/segments"
          >
            Cancel
          </Link>
          <PillButton
            type="button"
            variant="primary"
            disabled={state.saving || state.name.trim() === ''}
            onClick={() => void builder.save()}
          >
            {state.saving ? 'Saving…' : 'Save segment'}
          </PillButton>
        </div>
      </header>

      <Problems problems={state.problems} />

      <div className="grid gap-4 lg:grid-cols-[1fr_260px]">
        <div className="rounded-card border border-line bg-surface p-5">
          <label className="mb-1.5 block font-semibold text-[12px] text-t2" htmlFor="segment-name">
            Segment name
          </label>
          <input
            id="segment-name"
            value={state.name}
            onChange={(event) => builder.setName(event.target.value)}
            placeholder="Active customers"
            className="mb-5 block w-full max-w-sm rounded-field border border-line bg-surface px-3 py-2.5 text-[14px] text-t1"
          />

          <div className="mb-4 flex flex-wrap items-center gap-2 text-[13px] text-t2">
            <span>Match</span>
            <Choice
              options={[
                ['all', 'all (AND)'],
                ['any', 'any (OR)'],
              ]}
              value={state.definition.match}
              onChange={(value) => builder.setMatch(value as 'all' | 'any')}
            />
            <span>of the following</span>
          </div>

          <div className="space-y-2">
            {state.children.map((keyed, index) => (
              // Keyed by an id minted when the rule was created, not by
              // position: two identical rules are legal, and removing one from
              // the middle would otherwise move focus to a different row.
              <ChildRow
                key={keyed.key}
                keyed={keyed}
                declared={builder.declared}
                first={index === 0}
                match={state.definition.match}
                builder={builder}
              />
            ))}
          </div>

          <div className="mt-4 flex gap-2">
            <SmallButton onClick={builder.addRule}>Add rule</SmallButton>
            <SmallButton onClick={builder.addGroup}>Add “any of” group</SmallButton>
          </div>
        </div>

        <aside className="h-fit rounded-card border border-line bg-surface p-5">
          <h2 className="font-semibold text-[12px] text-t2">Reaches</h2>
          <p className="mt-1 font-bold font-num text-[26px] text-t1 tabular-nums">
            {/*
              A dash while unknown, never a zero. Zero is an answer — "this
              segment reaches nobody" — and showing it before the count arrives
              would talk someone out of a segment that is fine.
            */}
            {state.reach === undefined ? '—' : reachLabel(state.reach)}
          </p>
          <p className="mt-2 text-[12px] text-t3 leading-relaxed">
            Counted now, against your contacts as they are this moment. The audience is worked out
            again when a campaign is queued, so anyone who unsubscribes before then is left out.
          </p>
        </aside>
      </div>
    </>
  )
}

function ChildRow({
  keyed,
  declared,
  first,
  match,
  builder,
}: {
  readonly keyed: Keyed
  readonly declared: readonly ContactFieldDto[]
  readonly first: boolean
  readonly match: 'all' | 'any'
  readonly builder: ReturnType<typeof useSegmentBuilder>
}) {
  const joiner = first ? 'Where' : match === 'all' ? 'and' : 'or'

  if (isKeyedGroup(keyed)) {
    return (
      <div className="rounded-card border border-brand bg-brand-soft p-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="font-semibold text-[11px] text-brand-strong uppercase tracking-[0.06em]">
            {joiner} any of
          </span>
          <SmallButton onClick={() => builder.removeRule(keyed.key)}>Remove group</SmallButton>
        </div>
        <div className="space-y-2">
          {keyed.any.map((inner, at) => (
            <RuleRow
              key={inner.key}
              rule={inner.rule}
              declared={declared}
              joiner={at === 0 ? 'if' : 'or'}
              onChange={(patch) => builder.setRule(inner.key, patch)}
              onRemove={() => builder.removeRule(inner.key)}
            />
          ))}
        </div>
        <div className="mt-2">
          <SmallButton onClick={() => builder.addToGroup(keyed.key)}>Add to group</SmallButton>
        </div>
      </div>
    )
  }

  return (
    <RuleRow
      rule={keyed.rule}
      declared={declared}
      joiner={joiner}
      onChange={(patch) => builder.setRule(keyed.key, patch)}
      onRemove={() => builder.removeRule(keyed.key)}
    />
  )
}

function Choice({
  options,
  value,
  onChange,
}: {
  readonly options: readonly (readonly [string, string])[]
  readonly value: string
  readonly onChange: (value: string) => void
}) {
  return (
    <span className="inline-flex overflow-hidden rounded-pill border border-line">
      {options.map(([optionValue, label]) => (
        <button
          key={optionValue}
          type="button"
          onClick={() => onChange(optionValue)}
          className={`px-3 py-1 font-semibold text-[12px] ${
            value === optionValue ? 'bg-brand text-brand-contrast' : 'bg-surface text-t2'
          }`}
        >
          {label}
        </button>
      ))}
    </span>
  )
}

function SmallButton({
  onClick,
  children,
}: {
  readonly onClick: () => void
  readonly children: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-field border border-line bg-surface px-2.5 py-1.5 font-medium text-[12px] text-t2 hover:text-t1"
    >
      {children}
    </button>
  )
}
